// FUNCTIONALITY-115A — post-order payment panel with bottom-sheet confirmation.
// NOVAPAY-116 — NovaPay sandbox card payment link + status polling.
import { useEffect, useRef, useState } from "react";
import { customerOrderActionV2 } from "./order-actions-v2.js";
import { STORE_INFO } from "./store-info-v2.js";
import type { PaymentMethodChoice } from "./checkout-validation-v2.js";
import { emitEvent } from "./events-v2.js";
import { formatUah } from "./money-v2.js";
import {
  getPaymentReceiptStatusV2,
  makeReceiptIdempotencyKey,
  uploadPaymentReceiptV2,
} from "./payment-receipt-v2.js";
import { validatePaymentComment } from "./payment-link-security-v2.js";
import { PaymentConfirmationSheet } from "./PaymentConfirmationSheet.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { openHttpsSync } from "./external-link-v2.js";
import {
  abandonNovapayPaymentV2,
  createNovapayPaymentV2,
  getNovapayPaymentStatusV2,
  novapayStatusCopy,
  type NovapayPaymentPublic,
} from "./novapay-checkout-v2.js";
import { NovapayPaymentHelper } from "./novapay-payment-marks-v2.js";
import { NovapaySandboxBadge } from "./NovapaySandboxBadge.js";
import { markNovapayReturnExpected } from "./payment-lifecycle-v2.js";
import { buildHandoffGoUrl, openNovapayHandoffSync } from "./novapay-handoff-v2.js";
import { t } from "./store-v2-ui-copy.js";
import { isNovapayCheckoutDegradedStaging } from "./novapay-staging-degraded-v2.js";
import {
  isNovapayLiveCreateEnabled,
  isNovapayNotLiveError,
  isNovapayNotLiveUi,
  NOVAPAY_NOT_LIVE,
} from "./novapay-live-gate-v2.js";
import {
  fetchBankInstructionV2,
  pollAsiaStyleBankPaymentStatus,
  BANK_RESUME_POLL_MS,
  type BankPaymentClientPayload,
  getBankPaymentPurpose,
} from "./bank-iban-payment-v2.js";
import { BankPaymentDetailsV2 } from "./BankPaymentDetailsV2.js";
import { BankPayActionsV2 } from "./BankPayActionsV2.js";
import { createReceiptPreviewUrl } from "./receipt-preview-v2.js";
import { markBankPaymentConfirmed } from "./bank-launcher-telemetry-v2.js";
import { enabledCustomerCheckoutPaymentMethods } from "./customer-payment-flags-v2.js";
import { isPaidPaymentStatus } from "./customer-order-actions-registry-v2.js";
import { scheduleBoundedRetries } from "./payret-restore-v2.js";

const COMPILE_BANK_IBAN_ONLY =
  String(import.meta.env.VITE_CUSTOMER_PAYMENT_MODE ?? "").trim().toUpperCase() === "BANK_IBAN_ONLY";

export interface OrderPaymentPanelV2Props {
  lang: StoreV2Lang;
  sessionKey: string;
  orderId: string;
  displayOrderNumber?: string | null;
  contactName?: string | null;
  contactPhoneE164?: string | null;
  totalUah?: number | null;
  initialMethod?: PaymentMethodChoice | null;
  bankPayment?: BankPaymentClientPayload | null;
  /** R4B — parent success lifecycle listens for paid / pending / verifying. */
  onPaymentStatusChange?: (status: string | null) => void;
  /** When true, show verifying copy after hosted return before provider confirm. */
  verifyingReturn?: boolean;
  /** R5 — after order create, open hosted NovaPay without a second normal click. */
  autoStartNovapay?: boolean;
  /** R5 — hide entire panel chrome when parent already renders paid bento. */
  hideWhenPaid?: boolean;
  /** R5A — lock method change while payment is ambiguous. */
  allowMethodChange?: boolean;
  /** R7A — notify parent when FOP receipt is in manager review (not paid). */
  onFopReviewStateChange?: (inReview: boolean) => void;
  /** RC3 — open Legal Hub (payment section) from failure/retry surface. */
  onOpenLegalHub?: () => void;
  /**
   * R8-R2 — when true, hide check-status / abandon / back-to-order / slow-manager /
   * change-method buttons. Parent renders them once via getCustomerOrderActions.
   * CUSTOMER-COMMS-140-R1 — also hides inline FOP «Надіслати квитанцію» (registry owns it).
   */
  suppressRecoveryActions?: boolean;
  /** R8-R2 — expose pay CTA click for canonical “continue payment”. */
  payCtaRef?: { current: (() => void) | null };
  /** CUSTOMER-COMMS-140-R1 — expose FOP receipt review open for canonical send_receipt. */
  fopReceiptCtaRef?: { current: (() => void) | null };
  /** Keep attach-receipt on BANK paid; hide requisites once confirmed. */
  orderPaymentStatus?: string | null;
  /** HR01-R2 — bank timestamp outranks stale payment_in_review for paidVerified. */
  bankConfirmedAt?: string | null;
  /**
   * CUSTOMER-R4.15 — PAYMENT_COPY_DEDUPE: when parent StatusHero already shows
   * bank_awaiting_body, skip the duplicate fop_after_pay_instruction paragraph.
   */
  suppressPaymentIntro?: boolean;
  /** K20 — after STG pay-now, attempt bank app once. Never on order reopen. */
  autoAttemptBankHandoff?: boolean;
};

type PanelStatus =
  | "idle"
  | "sending"
  | "reported"
  | "method_changed"
  | "help_sent"
  | "receipt_uploaded"
  | "receipt_fallback"
  | "error";

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const RECEIPT_MIME = new Set(["image/jpeg", "image/png", "image/webp", "application/pdf"]);

function isAllowedReceiptFile(file: File): boolean {
  if (RECEIPT_MIME.has(file.type)) return true;
  // Telegram Android WebView often omits MIME — accept by extension.
  if (!file.type || file.type === "application/octet-stream") {
    return /\.(jpe?g|png|webp|pdf)$/i.test(file.name);
  }
  return /\.pdf$/i.test(file.name);
}

function formatKyivClock(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "Europe/Kyiv",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function canonicalBankPurpose(displayNumber: string | null | undefined): string | null {
  return getBankPaymentPurpose(displayNumber);
}

export function OrderPaymentPanelV2({
  lang,
  sessionKey,
  orderId,
  displayOrderNumber,
  contactName,
  contactPhoneE164,
  totalUah,
  initialMethod = null,
  bankPayment = null,
  onPaymentStatusChange,
  verifyingReturn = false,
  autoStartNovapay = false,
  hideWhenPaid = true,
  allowMethodChange = true,
  onFopReviewStateChange,
  onOpenLegalHub,
  suppressRecoveryActions = false,
  payCtaRef,
  fopReceiptCtaRef,
  orderPaymentStatus,
  bankConfirmedAt: bankConfirmedAtProp = null,
  suppressPaymentIntro = false,
  autoAttemptBankHandoff = false,
}: OrderPaymentPanelV2Props) {
  const [status, setStatus] = useState<PanelStatus>("idle");
  const [method, setMethod] = useState<string | null>(initialMethod);
  const [msg, setMsg] = useState<string | null>(null);
  const [paymentLocked, setPaymentLocked] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [paymentComment, setPaymentComment] = useState("");
  const [receiptIdempotencyKey, setReceiptIdempotencyKey] = useState<string | null>(null);
  const [receiptDeliveryStatus, setReceiptDeliveryStatus] = useState<string | null>(null);
  const [novapay, setNovapay] = useState<NovapayPaymentPublic | null>(null);
  const [novapayBusy, setNovapayBusy] = useState(false);
  const [novapayMsg, setNovapayMsg] = useState<string | null>(null);
  const [hostedOpenFailed, setHostedOpenFailed] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<"idle" | "uploading" | "retrying">("idle");
  const [slowPayment, setSlowPayment] = useState(false);
  const [preparingHint, setPreparingHint] = useState(false);
  const [hostedOpened, setHostedOpened] = useState(false);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [instruction, setInstruction] = useState<BankPaymentClientPayload | null>(bankPayment);
  const [bankChecking, setBankChecking] = useState(false);
  const [bankStillPending, setBankStillPending] = useState(false);
  const [bankCreatedAt, setBankCreatedAt] = useState<string | null>(null);
  const [bankConfirmedAt, setBankConfirmedAt] = useState<string | null>(
    bankConfirmedAtProp ? String(bankConfirmedAtProp) : null,
  );
  const novapayClickGuard = useRef(false);
  const autoStartedRef = useRef(false);
  const uploadGuard = useRef(false);
  const previewUrlRef = useRef<string | null>(null);
  const pendingSinceRef = useRef<number | null>(null);
  const createPhaseTimers = useRef<number[]>([]);

  const orderNo = displayOrderNumber?.trim() || orderId.slice(0, 12);
  void contactName;
  void contactPhoneE164;
  const purpose =
    instruction?.purpose ??
    canonicalBankPurpose(displayOrderNumber) ??
    (lang === "en" ? "Payment purpose is issued after the order number exists." : "Призначення з’явиться після створення номера замовлення.");
  const amountLabel = instruction
    ? `${instruction.amount_uah} ₴`
    : totalUah != null && Number.isFinite(totalUah)
      ? formatUah(Math.ceil(totalUah))
      : "—";
  const amountRaw = instruction?.amount_uah ?? String(Math.ceil(Number(totalUah) || 0));

  const receiptFallbackMessage =
    lang === "en"
      ? "Could not upload the file. Send a screenshot in chat as a reply to the order message."
      : "Не вдалося надіслати файл. Надішліть скрін у чат відповіддю на повідомлення з номером замовлення.";

  useEffect(() => {
    let cancelled = false;
    let savedKey: string | null = null;
    try {
      savedKey = window.localStorage.getItem(`asiastyle.store-v2.receipt.${orderId}`);
    } catch {
      /* storage unavailable */
    }
    if (savedKey) {
      setReceiptIdempotencyKey(savedKey);
      void getPaymentReceiptStatusV2({ orderId, sessionKey, idempotencyKey: savedKey }).then((result) => {
        if (cancelled || result.accepted !== true) return;
        setStatus("receipt_uploaded");
        setPaymentLocked(true);
        setReceiptDeliveryStatus(result.manager_delivery_status ?? "pending");
        setMsg(null);
        onFopReviewStateChange?.(true);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [lang, orderId, sessionKey, onFopReviewStateChange]);

  useEffect(() => {
    if (bankPayment) setInstruction(bankPayment);
  }, [bankPayment]);

  useEffect(() => {
    if (method !== "bank_transfer_fop" || instruction) return;
    let cancelled = false;
    void fetchBankInstructionV2({ sessionKey, orderId }).then((loaded) => {
      if (!cancelled && loaded) setInstruction(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [instruction, method, orderId, sessionKey]);

  useEffect(() => {
    if (method !== "bank_transfer_fop") return;
    let cancelled = false;
    const applyResult = (result: {
      paid: boolean;
      created_at: string | null;
      bank_confirmed_at: string | null;
    }) => {
      if (result.created_at) setBankCreatedAt(result.created_at);
      if (result.bank_confirmed_at) setBankConfirmedAt(result.bank_confirmed_at);
      if (result.paid) {
        setBankStillPending(false);
        setBankChecking(false);
        markBankPaymentConfirmed();
      }
    };
    const poll = () => {
      void pollAsiaStyleBankPaymentStatus({ sessionKey, orderId }).then((result) => {
        if (cancelled) return;
        applyResult(result);
      });
    };
    poll();
    let leftApp = false;
    let cancelResume: (() => void) | null = null;
    const onPageHide = () => {
      leftApp = true;
    };
    const onReturn = () => {
      if (document.visibilityState === "hidden") {
        leftApp = true;
        return;
      }
      if (!leftApp && document.visibilityState !== "visible") return;
      leftApp = false;
      setBankChecking(true);
      setBankStillPending(false);
      cancelResume?.();
      let ticks = 0;
      cancelResume = scheduleBoundedRetries(() => {
        ticks += 1;
        void pollAsiaStyleBankPaymentStatus({ sessionKey, orderId }).then((result) => {
          if (cancelled) return;
          applyResult(result);
          if (result.paid) {
            setBankChecking(false);
            return;
          }
          if (ticks >= BANK_RESUME_POLL_MS.length) {
            setBankChecking(false);
            setBankStillPending(true);
          }
        });
      }, BANK_RESUME_POLL_MS);
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      cancelled = true;
      cancelResume?.();
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [method, orderId, sessionKey]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (method !== "novapay_card") return;
    let cancelled = false;
    let polls = 0;
    const MAX_POLLS = 12; // ~48s bounded — never poll forever (R2-R5)
    if (pendingSinceRef.current == null) pendingSinceRef.current = Date.now();
    // RC3: meaningful status at 2s / 8s — never endless spinner.
    const preparingTimer = window.setTimeout(() => {
      if (!cancelled) setPreparingHint(true);
    }, 2_000);
    const slowTimer = window.setTimeout(() => {
      if (!cancelled) setSlowPayment(true);
    }, 8_000);
    const poll = async () => {
      if (polls >= MAX_POLLS) return;
      polls += 1;
      const res = await getNovapayPaymentStatusV2({
        orderId,
        sessionKey,
        paymentId: novapay?.payment_id ?? null,
      });
      if (cancelled || !res.ok || !res.payment) return;
      // Never overwrite a confirmed paid state with a stale pending response.
      if (novapay?.status === "paid" && res.payment.status !== "paid") return;
      setNovapay(res.payment);
      if (res.correlation_id || res.payment.correlation_id) {
        setCorrelationId(res.correlation_id ?? res.payment.correlation_id ?? null);
      }
      const statusMsg =
        verifyingReturn && res.payment.status !== "paid"
          ? lang === "en"
            ? "Confirming payment…"
            : "Підтверджуємо оплату…"
          : novapayStatusCopy(lang, res.payment.customer_lifecycle ?? res.payment.status);
      setNovapayMsg(statusMsg);
      onPaymentStatusChange?.(res.payment.status);
      if (res.payment.status === "paid") {
        setPaymentLocked(true);
        setSlowPayment(false);
        setPreparingHint(false);
      }
      emitEvent("payment_novapay_status_poll", {
        order_id: orderId,
        payment_id: res.payment.payment_id,
        correlation_id: res.payment.correlation_id ?? null,
        status: res.payment.status,
        status_check_ms: res.timings?.status_check_ms ?? null,
        webhook_delay_ms: res.timings?.webhook_delay_ms ?? null,
      });
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearTimeout(slowTimer);
      window.clearTimeout(preparingTimer);
    };
  }, [lang, method, orderId, sessionKey, novapay?.payment_id, verifyingReturn, onPaymentStatusChange]);

  const clearCreatePhaseTimers = () => {
    for (const id of createPhaseTimers.current) window.clearTimeout(id);
    createPhaseTimers.current = [];
  };

  const startNovapay = async (opts?: { fromAuto?: boolean }) => {
    if (novapayBusy || novapayClickGuard.current || paymentLocked) return;
    if (novapay?.status === "paid") return;
    // 168A-R2 — never pretend create/send succeeded while production live-create is OFF.
    if (isNovapayNotLiveUi()) {
      setNovapayMsg(t(lang, "payment_novapay_not_live_body"));
      setHostedOpenFailed(false);
      emitEvent("payment_novapay_not_live", {
        order_id: orderId,
        reason: isNovapayCheckoutDegradedStaging() ? "degraded" : NOVAPAY_NOT_LIVE,
        live_create: isNovapayLiveCreateEnabled(),
      });
      return;
    }
    // RC7 — staging degraded: never create a new NovaPay session from UI.
    if (isNovapayCheckoutDegradedStaging()) {
      setNovapayMsg(t(lang, "payment_novapay_degraded_body"));
      setHostedOpenFailed(false);
      return;
    }
    // RC3: reuse valid pending / creating attempt — still call create (server dedupes).
    novapayClickGuard.current = true;
    setNovapayBusy(true);
    setHostedOpenFailed(false);
    setHostedOpened(false);
    setSlowPayment(false);
    setPreparingHint(false);
    pendingSinceRef.current = Date.now();
    setNovapayMsg(t(lang, "payment_preparing_novapay"));
    clearCreatePhaseTimers();
    createPhaseTimers.current.push(
      window.setTimeout(() => setPreparingHint(true), 2_000),
      window.setTimeout(() => {
        setSlowPayment(true);
        setNovapayMsg(t(lang, "payment_novapay_slow"));
      }, 8_000),
    );
    emitEvent("payment_novapay_create_requested", {
      order_id: orderId,
      auto: opts?.fromAuto === true,
      correlation_id: correlationId,
    });
    try {
      const res = await createNovapayPaymentV2({
        orderId,
        sessionKey,
        idempotencyKey: `ui:${orderId}:novapay`,
        correlationId,
      });
      if (res.correlation_id) setCorrelationId(res.correlation_id);
      emitEvent("payment_novapay_timing", {
        order_id: orderId,
        correlation_id: res.correlation_id ?? null,
        session_create_ms: res.timings?.session_create_ms ?? res.provider_ms ?? null,
        provider_request_ms: res.timings?.provider_request_ms ?? null,
        payment_url_ready_ms: res.timings?.payment_url_ready_ms ?? null,
        duplicate: res.duplicate === true,
        creating: res.creating === true,
      });
      if (!res.ok || !res.payment?.payment_url) {
        // Still creating (duplicate without URL) — keep bounded UI, do not invent a second charge.
        if (res.ok && res.creating && res.payment) {
          setNovapay(res.payment);
          setNovapayMsg(t(lang, "payment_preparing_novapay"));
          return;
        }
        setHostedOpenFailed(true);
        const providerDown =
          res.error === "provider_timeout" ||
          res.error === "network_error" ||
          res.error === "sandbox_credentials_missing" ||
          res.error === "provider_unavailable";
        setNovapayMsg(
          isNovapayNotLiveError(res.error)
            ? t(lang, "payment_novapay_not_live_body")
            : res.error === "sandbox_credentials_missing"
            ? lang === "en"
              ? "Card payment is temporarily unavailable. Use FOP or COD, or contact the manager."
              : "Оплата карткою тимчасово недоступна. Оберіть ФОП / післяплату або напишіть менеджеру."
            : res.error === "provider_timeout" || res.error === "provider_unavailable"
              ? t(lang, "payment_novapay_slow")
              : lang === "en"
                ? "Could not start online payment. Order is saved — try again or choose another method."
                : "Не вдалося відкрити оплату. Замовлення збережено — спробуйте ще раз або оберіть інший спосіб.",
        );
        emitEvent("payment_novapay_create_failed", {
          order_id: orderId,
          error: res.error ?? "unknown",
          provider_ms: res.provider_ms ?? null,
          provider_down: providerDown,
          correlation_id: res.correlation_id ?? null,
        });
        setNovapay(res.payment ?? null);
        setSlowPayment(true);
        return;
      }
      clearCreatePhaseTimers();
      setPreparingHint(false);
      setSlowPayment(false);
      emitEvent("payment_novapay_create_ok", {
        order_id: orderId,
        payment_id: res.payment.payment_id,
        provider_ms: res.provider_ms ?? null,
        status: res.payment.status,
        correlation_id: res.correlation_id ?? res.payment.correlation_id ?? null,
        duplicate: res.duplicate === true,
      });
      setNovapay(res.payment);
      setNovapayMsg(novapayStatusCopy(lang, res.payment.customer_lifecycle ?? res.payment.status));
      setMethod("novapay_card");
      // RC3: once URL is ready, unblock Mini App — recovery screen, not endless busy.
      setNovapayBusy(false);
      const opened = openNovapayHandoffSync({
        orderId,
        paymentUrl: res.payment.payment_url,
        handoffUrl: buildHandoffGoUrl(orderId, sessionKey),
      });
      if (opened.kind !== "opened") {
        const fallback = openHttpsSync(res.payment.payment_url);
        if (fallback.kind !== "opened") {
          setHostedOpenFailed(true);
          setHostedOpened(false);
          setNovapayMsg(
            lang === "en"
              ? "Could not open payment automatically. Tap the button to continue."
              : "Не вдалося автоматично відкрити оплату. Натисніть кнопку, щоб продовжити.",
          );
        } else {
          markNovapayReturnExpected(orderId);
          setHostedOpened(true);
          setHostedOpenFailed(false);
          setNovapayMsg(
            lang === "en"
              ? "Payment page opened. You can check status or return to the order."
              : "Сторінку оплати відкрито. Можете перевірити статус або повернутися до замовлення.",
          );
        }
      } else {
        markNovapayReturnExpected(orderId);
        setHostedOpened(true);
        setHostedOpenFailed(false);
        setNovapayMsg(
          lang === "en"
            ? "Payment page opened. You can check status or return to the order."
            : "Сторінку оплати відкрито. Можете перевірити статус або повернутися до замовлення.",
        );
      }
    } finally {
      setNovapayBusy(false);
      clearCreatePhaseTimers();
      window.setTimeout(() => {
        novapayClickGuard.current = false;
      }, 1200);
    }
  };

  useEffect(() => {
    if (!payCtaRef) return;
    payCtaRef.current = () => {
      void startNovapay();
    };
    return () => {
      payCtaRef.current = null;
    };
  });

  useEffect(() => {
    if (!fopReceiptCtaRef) return;
    fopReceiptCtaRef.current = () => {
      setReviewOpen(true);
    };
    return () => {
      fopReceiptCtaRef.current = null;
    };
  });

  const checkPaymentStatus = async () => {
    const res = await getNovapayPaymentStatusV2({
      orderId,
      sessionKey,
      paymentId: novapay?.payment_id ?? null,
    });
    if (res.ok && res.payment) {
      setNovapay(res.payment);
      const providerSt = String(res.payment.provider_status ?? "").toLowerCase();
      const processing =
        providerSt === "processing" ||
        providerSt === "created" ||
        res.payment.status === "pending" ||
        res.payment.status === "link_ready";
      setNovapayMsg(
        processing && res.payment.status !== "paid"
          ? t(lang, "payment_novapay_processing")
          : novapayStatusCopy(lang, res.payment.customer_lifecycle ?? res.payment.status),
      );
      onPaymentStatusChange?.(res.payment.status);
      if (res.payment.status === "paid") {
        setPaymentLocked(true);
        setSlowPayment(false);
      }
    } else {
      setNovapayMsg(
        lang === "en"
          ? "Could not refresh payment status. Try again or contact the manager."
          : "Не вдалося оновити статус. Спробуйте ще раз або напишіть менеджеру.",
      );
    }
  };

  const abandonPayment = async () => {
    if (paymentLocked || novapay?.status === "paid") return;
    setNovapayBusy(true);
    try {
      // RC4 — final paid-state protection before abandon (never cancel a paid order).
      const pre = await getNovapayPaymentStatusV2({
        orderId,
        sessionKey,
        paymentId: novapay?.payment_id ?? null,
      });
      if (pre.ok && pre.payment?.status === "paid") {
        setNovapay(pre.payment);
        setPaymentLocked(true);
        setNovapayMsg(novapayStatusCopy(lang, "paid"));
        onPaymentStatusChange?.("paid");
        return;
      }
      const res = await abandonNovapayPaymentV2({
        orderId,
        sessionKey,
        paymentId: novapay?.payment_id ?? null,
      });
      if (res.ok && res.payment) {
        setNovapay(res.payment);
        setHostedOpened(false);
        setNovapayMsg(novapayStatusCopy(lang, "abandoned"));
        emitEvent("payment_novapay_abandoned", {
          order_id: orderId,
          payment_id: res.payment.payment_id,
          correlation_id: res.correlation_id ?? null,
          order_preserved: res.order_preserved !== false,
        });
      } else if (res.error === "order_already_paid" && res.payment) {
        setNovapay(res.payment);
        setPaymentLocked(true);
        setNovapayMsg(novapayStatusCopy(lang, "paid"));
        onPaymentStatusChange?.("paid");
      } else {
        setNovapayMsg(
          lang === "en"
            ? "Could not cancel the payment attempt. Order is still saved."
            : "Не вдалося відмінити спробу оплати. Замовлення збережено.",
        );
      }
    } finally {
      setNovapayBusy(false);
    }
  };

  useEffect(() => {
    if (!autoStartNovapay || method !== "novapay_card" || verifyingReturn) return;
    if (autoStartedRef.current || novapay?.status === "paid" || paymentLocked) return;
    if (isNovapayNotLiveUi()) {
      autoStartedRef.current = true;
      setNovapayMsg(t(lang, "payment_novapay_not_live_body"));
      return;
    }
    autoStartedRef.current = true;
    void startNovapay({ fromAuto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot handoff per order mount
  }, [autoStartNovapay, method, orderId, verifyingReturn]);

  const run = async (
    action: "payment_reported" | "change_payment_method" | "manager_help",
    paymentMethod?: string,
  ) => {
    setStatus("sending");
    setMsg(null);
    if (action === "manager_help") emitEvent("payment_help_requested", { order_id: orderId });
    const res = await customerOrderActionV2({ sessionKey, orderId, action, paymentMethod: paymentMethod ?? null });
    if (!res.ok && action !== "manager_help") {
      setStatus("error");
      setMsg(res.message ?? (lang === "en" ? "Action failed. Try again." : "Не вдалося виконати дію. Спробуйте ще раз."));
      return;
    }
    if (action === "payment_reported") {
      setStatus("reported");
      setPaymentLocked(true);
      setReviewOpen(false);
    } else if (action === "manager_help") {
      setStatus("help_sent");
      setMsg(
        res.message ??
          (lang === "en"
            ? "Request sent. A manager will contact you."
            : "Запит передано. Менеджер зв’яжеться з вами."),
      );
    } else {
      setStatus("method_changed");
      if (paymentMethod) setMethod(paymentMethod);
    }
  };

  const onReceiptSelected = (file: File | null) => {
    setMsg(null);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setReceiptFile(null);
    setReceiptPreview(null);
    if (!file) return;
    const safeName = file.name.replace(/[^\w.\-() ]+/g, "_").slice(0, 80);
    if (!file.size) {
      setStatus("error");
      setMsg(lang === "en" ? "Empty file is not allowed." : "Порожній файл не дозволено.");
      return;
    }
    if (!isAllowedReceiptFile(file)) {
      setStatus("error");
      setMsg(lang === "en" ? "Only JPEG, PNG, WebP or PDF are allowed." : "Дозволені JPEG, PNG, WebP або PDF.");
      return;
    }
    if (file.size > MAX_RECEIPT_BYTES) {
      setStatus("error");
      setMsg(lang === "en" ? "File is too large. Max 10 MB." : "Файл занадто великий. Максимум 10 МБ.");
      return;
    }
    const type = RECEIPT_MIME.has(file.type) ? file.type : /\.pdf$/i.test(file.name) ? "application/pdf" : file.type;
    const normalized = new File([file], safeName || (type === "application/pdf" ? "receipt.pdf" : "receipt.jpg"), { type });
    setReceiptFile(normalized);
    setReceiptIdempotencyKey(makeReceiptIdempotencyKey(orderId, normalized));
    if (type === "application/pdf") {
      setReceiptPreview(null);
      emitEvent("payment_receipt_upload_requested", { order_id: orderId, filename: normalized.name });
      return;
    }
    void createReceiptPreviewUrl(normalized).then((url) => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setReceiptPreview(url);
    });
    emitEvent("payment_receipt_upload_requested", { order_id: orderId, filename: normalized.name });
  };

  const uploadReceipt = async () => {
    if (!receiptFile || uploadGuard.current) return;
    uploadGuard.current = true;
    const idempotencyKey = receiptIdempotencyKey ?? makeReceiptIdempotencyKey(orderId, receiptFile);
    setReceiptIdempotencyKey(idempotencyKey);
    setStatus("sending");
    setUploadProgress("uploading");
    setMsg(null);
    try {
      let res = await uploadPaymentReceiptV2({
        orderId,
        sessionKey,
        file: receiptFile,
        paymentComment,
        idempotencyKey,
      });
      if (!res.ok && (res.error === "timeout" || res.error === "network_error")) {
        setUploadProgress("retrying");
        const recovered = await getPaymentReceiptStatusV2({ orderId, sessionKey, idempotencyKey });
        if (recovered.accepted === true) {
          res = { ...res, ...recovered, ok: true, accepted: true };
        } else {
          // Keep selected file — user can retry without re-picking.
          setStatus("receipt_fallback");
          setMsg(
            lang === "en"
              ? "Network error. Your file is still selected — tap Send again."
              : "Помилка мережі. Файл залишився вибраним — натисніть «Надіслати» ще раз.",
          );
          emitEvent("payment_receipt_upload_unavailable", {
            order_id: orderId,
            reason: res.error ?? "network_error",
          });
          emitEvent("payment_receipt_upload_failed", {
            order_id: orderId,
            reason: res.error ?? "network_error",
          });
          return;
        }
      }
      if (res.ok && res.accepted !== false) {
        setStatus("receipt_uploaded");
        setPaymentLocked(true);
        setReviewOpen(false);
        setPaymentComment("");
        setReceiptDeliveryStatus(res.manager_delivery_status ?? "pending");
        try {
          window.localStorage.setItem(`asiastyle.store-v2.receipt.${orderId}`, idempotencyKey);
        } catch {
          /* storage unavailable */
        }
        setMsg(null);
        onFopReviewStateChange?.(true);
        emitEvent("payment_receipt_uploaded", {
          order_id: orderId,
          receipt_id: res.receipt_id ?? null,
          event_id: res.event_id ?? null,
          manager_delivery_status: res.manager_delivery_status ?? null,
          duplicate: res.duplicate === true || res.deduped === true,
        });
        return;
      }
      // Failure: keep file + preview so the customer can retry.
      setStatus("receipt_fallback");
      setMsg(res.user_message ?? res.message_uk ?? receiptFallbackMessage);
      emitEvent("payment_receipt_upload_unavailable", {
        order_id: orderId,
        reason: res.required_binding ?? res.error ?? "upload_failed",
      });
      emitEvent("payment_receipt_upload_failed", {
        order_id: orderId,
        reason: res.required_binding ?? res.error ?? "upload_failed",
      });
    } finally {
      setUploadProgress("idle");
      uploadGuard.current = false;
    }
  };

  const submitPaymentReview = async () => {
    const validation = validatePaymentComment(paymentComment);
    if (!validation.ok) {
      setStatus("error");
      setMsg(validation.message);
      return;
    }
    // Already locked + receipt retry: upload only (do not re-fire payment_reported).
    if (paymentLocked && receiptFile && status !== "receipt_uploaded") {
      await uploadReceipt();
      return;
    }
    setStatus("sending");
    setMsg(null);
    const report = await customerOrderActionV2({
      sessionKey,
      orderId,
      action: "payment_reported",
      paymentComment: validation.value || null,
      hasReceipt: Boolean(receiptFile),
    });
    if (!report.ok) {
      setStatus("error");
      setMsg(report.message ?? (lang === "en" ? "Could not submit payment for review." : "Не вдалося передати оплату на перевірку."));
      return;
    }
    setPaymentLocked(true);
    if (receiptFile) await uploadReceipt();
    else {
      setStatus("reported");
      setReviewOpen(false);
      setMsg(null);
      onFopReviewStateChange?.(true);
    }
  };

  const isFop = method === "bank_transfer_fop";
  const isCod = method === "nova_poshta_cod";
  const isNovapay = method === "novapay_card";
  const paidVerified =
    novapay?.status === "paid" ||
    isPaidPaymentStatus(orderPaymentStatus, bankConfirmedAtProp ?? bankConfirmedAt);
  if (hideWhenPaid && paidVerified && method !== "bank_transfer_fop") {
    return null;
  }
  const paymentFailed =
    novapay?.status === "failed" ||
    novapay?.status === "expired" ||
    novapay?.status === "cancelled" ||
    novapay?.customer_lifecycle === "abandoned";
  const showChangeMethod =
    !suppressRecoveryActions &&
    allowMethodChange &&
    enabledCustomerCheckoutPaymentMethods().length > 1 &&
    !paidVerified &&
    !paymentLocked &&
    !verifyingReturn &&
    status !== "reported" &&
    status !== "receipt_uploaded";
  const providerProcessing =
    String(novapay?.provider_truth_state ?? "") === "provider_processing" ||
    String(novapay?.provider_status ?? "").toLowerCase() === "processing" ||
    String(novapay?.provider_status ?? "")
      .toLowerCase()
      .includes("processing");
  const checkoutDegraded = isNovapayCheckoutDegradedStaging();
  const novapayNotLive = isNovapayNotLiveUi();
  const showNovapayFallbackCta =
    isNovapay &&
    !paidVerified &&
    !verifyingReturn &&
    !checkoutDegraded &&
    !novapayNotLive &&
    !providerProcessing &&
    (hostedOpenFailed ||
      !autoStartNovapay ||
      novapay?.status === "expired" ||
      novapay?.status === "failed" ||
      novapay?.status === "link_ready" ||
      novapay?.status === "pending");
  const openReviewLabel =
    status === "receipt_uploaded"
      ? lang === "en"
        ? "View / replace receipt"
        : "Переглянути / Замінити"
      : t(lang, "fop_send_receipt");
  // PAYMENT_COPY_DEDUPE — single canonical explanation after order create.
  const afterPayHint =
    isFop && !suppressPaymentIntro ? t(lang, "fop_after_pay_instruction") : null;
  const showFopReceiptCta = isFop && !reviewOpen && !paidVerified && !suppressRecoveryActions;

  return (
    <div className="sv2-pay-panel" style={{ marginTop: 16, textAlign: "left" }}>
      <div className="sv2-section-title">{lang === "en" ? "Payment" : "Оплата"}</div>
      {isFop && afterPayHint && !paidVerified ? <p className="sv2-muted">{afterPayHint}</p> : null}

      {isNovapay && !COMPILE_BANK_IBAN_ONLY ? (
        <div className="sv2-info-card" style={{ marginBottom: 12 }} data-testid="novapay-panel">
          <div className="sv2-muted" style={{ marginBottom: 8 }} data-testid="sv2-novapay-status-msg">
            {providerProcessing && !paidVerified ? (
              <div data-testid="sv2-novapay-provider-processing" style={{ marginBottom: 8 }}>
                <strong>{t(lang, "payment_novapay_processing")}</strong>
                <p style={{ margin: "6px 0 0" }}>{t(lang, "payment_novapay_processing_body")}</p>
              </div>
            ) : null}
            {checkoutDegraded && !paidVerified && !providerProcessing ? (
              <div data-testid="sv2-novapay-panel-degraded" style={{ marginBottom: 8 }}>
                <strong>{t(lang, "payment_novapay_degraded_title")}</strong>
                <div>{t(lang, "payment_novapay_degraded_state")}</div>
                <p style={{ margin: "6px 0 0" }}>{t(lang, "payment_novapay_degraded_body")}</p>
              </div>
            ) : null}
            {novapayNotLive && !checkoutDegraded && !paidVerified && !providerProcessing ? (
              <div data-testid="sv2-novapay-panel-not-live" style={{ marginBottom: 8 }}>
                <strong>{t(lang, "payment_novapay_not_live_title")}</strong>
                <div>{t(lang, "payment_novapay_not_live_state")}</div>
                <p style={{ margin: "6px 0 0" }}>{t(lang, "payment_novapay_not_live_body")}</p>
                {allowMethodChange ? (
                  <button
                    type="button"
                    className="sv2-cta sv2-primary-commerce"
                    data-testid="sv2-novapay-use-fop-fallback"
                    style={{ marginTop: 10 }}
                    disabled={status === "sending"}
                    onClick={() => void run("change_payment_method", "bank_transfer_fop")}
                  >
                    {t(lang, "payment_novapay_use_fop_fallback")}
                  </button>
                ) : null}
              </div>
            ) : null}
            {paymentFailed && !paidVerified && !providerProcessing ? (
              <strong data-testid="sv2-novapay-failed-title">
                {lang === "en" ? "Payment not completed" : "Оплату не завершено"}
              </strong>
            ) : null}
            {preparingHint && novapayBusy && !paidVerified && !paymentFailed && !slowPayment ? (
              <div data-testid="sv2-novapay-preparing" style={{ marginBottom: 8 }}>
                <strong>{t(lang, "payment_preparing_novapay")}</strong>
              </div>
            ) : null}
            {slowPayment && !paidVerified && !paymentFailed ? (
              <div data-testid="sv2-novapay-slow" style={{ marginBottom: 8 }}>
                <strong>{t(lang, "payment_novapay_slow")}</strong>
              </div>
            ) : null}
            {verifyingReturn && !paidVerified
              ? lang === "en"
                ? "We received a return from payment. Waiting for payment status confirmation."
                : "Отримали повернення з оплати. Очікуємо підтвердження статусу платежу."
              : (novapayMsg ?? novapayStatusCopy(lang, novapay?.customer_lifecycle ?? novapay?.status))}
          </div>
          {paidVerified ? (
            <div className="sv2-alert sv2-alert-ok" data-testid="sv2-novapay-paid">
              {novapayStatusCopy(lang, "paid")}
            </div>
          ) : hostedOpened && !paymentFailed && !suppressRecoveryActions ? (
            <div className="sv2-pay-actions" data-testid="sv2-novapay-recovery" style={{ display: "grid", gap: 8 }}>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-novapay-check-status"
                disabled={novapayBusy}
                onClick={() => void checkPaymentStatus()}
              >
                {t(lang, "check_payment_status")}
              </button>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-novapay-back-order"
                onClick={() => emitEvent("payment_novapay_back_to_order", { order_id: orderId })}
              >
                {t(lang, "return_to_order")}
              </button>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-novapay-abandon"
                disabled={novapayBusy || paymentLocked}
                onClick={() => void abandonPayment()}
              >
                {t(lang, "abandon_payment")}
              </button>
              <NovapaySandboxBadge lang={lang} testId="sv2-payment-panel-sandbox-badge" />
            </div>
          ) : hostedOpened && !paymentFailed && suppressRecoveryActions ? (
            <div data-testid="sv2-novapay-recovery-suppressed" className="sv2-muted" style={{ fontSize: 13 }}>
              <NovapaySandboxBadge lang={lang} testId="sv2-payment-panel-sandbox-badge" />
            </div>
          ) : showNovapayFallbackCta || slowPayment || hostedOpenFailed ? (
            <>
              <button
                type="button"
                className="sv2-cta sv2-novapay-pay-option"
                data-testid="sv2-novapay-pay-cta"
                disabled={novapayBusy || paymentLocked}
                onClick={() => void startNovapay()}
              >
                {novapayBusy ? (
                  lang === "en" ? (
                    "Loading…"
                  ) : (
                    "Завантаження…"
                  )
                ) : slowPayment || novapay?.status === "expired" || novapay?.status === "failed" ? (
                  t(lang, "payment_retry")
                ) : (
                  <>
                    {lang === "en" ? "⚡ Continue to payment" : "⚡ Перейти до оплати"}
                    {amountLabel ? ` — ${amountLabel}` : ""}
                  </>
                )}
              </button>
              {!checkoutDegraded && !paidVerified ? (
                <button
                  type="button"
                  className="sv2-cta sv2-secondary"
                  data-testid="sv2-novapay-send-telegram-link"
                  disabled={status === "sending" || novapayBusy || paymentLocked}
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    void (async () => {
                      setStatus("sending");
                      setMsg(null);
                      const res = await customerOrderActionV2({
                        sessionKey,
                        orderId,
                        action: "send_novapay_payment_link",
                      });
                      if (res.ok) {
                        setStatus("idle");
                        const delivery = String(res.delivery_state ?? "").toLowerCase();
                        setMsg(
                          delivery === "recently_sent" || res.duplicate
                            ? lang === "en"
                              ? "Payment link already sent recently"
                              : "Посилання вже надіслано нещодавно"
                            : (res.message ??
                              (lang === "en"
                                ? "Payment link sent to Telegram"
                                : "Посилання на оплату надіслано в Telegram")),
                        );
                      } else {
                        setStatus("error");
                        setMsg(
                          res.message ??
                            (lang === "en"
                              ? "Could not send. Try again"
                              : "Не вдалося надіслати. Спробувати ще раз"),
                        );
                      }
                    })();
                  }}
                >
                  {status === "sending"
                    ? lang === "en"
                      ? "Sending…"
                      : "Надсилаємо…"
                    : lang === "en"
                      ? "📩 Send link in Telegram"
                      : "📩 Надіслати посилання в Telegram"}
                </button>
              ) : null}
              {msg && isNovapay ? (
                <p
                  className={status === "error" ? "sv2-error" : "sv2-muted"}
                  data-testid="sv2-novapay-send-feedback"
                  style={{ fontSize: 13, marginTop: 8 }}
                >
                  {msg}
                </p>
              ) : null}
              {slowPayment && !suppressRecoveryActions ? (
                <div className="sv2-pay-actions" style={{ display: "grid", gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    className="sv2-cta sv2-secondary"
                    data-testid="sv2-novapay-slow-back-order"
                    onClick={() => emitEvent("payment_novapay_back_to_order", { order_id: orderId })}
                  >
                    {t(lang, "return_to_order")}
                  </button>
                  <button
                    type="button"
                    className="sv2-cta sv2-secondary"
                    data-testid="sv2-novapay-slow-manager"
                    disabled={status === "sending"}
                    onClick={() => void run("manager_help")}
                  >
                    {t(lang, "write_manager_primary")}
                  </button>
                </div>
              ) : null}
              {hostedOpenFailed ? (
                <p className="sv2-muted" style={{ marginTop: 8, fontSize: 13 }}>
                  {lang === "en"
                    ? "Could not open payment automatically. Tap the button to continue."
                    : "Не вдалося автоматично відкрити оплату. Натисніть кнопку, щоб продовжити."}
                </p>
              ) : !slowPayment ? (
                <>
                  <NovapaySandboxBadge lang={lang} testId="sv2-payment-panel-sandbox-badge" />
                  <NovapayPaymentHelper lang={lang} />
                </>
              ) : null}
            </>
          ) : novapayNotLive ? (
            <p className="sv2-muted" data-testid="sv2-novapay-not-live-hint" style={{ fontSize: 13 }}>
              {t(lang, "payment_novapay_not_live_body")}
            </p>
          ) : novapayBusy || autoStartNovapay ? (
            <p className="sv2-muted" data-testid="sv2-novapay-autostart">
              {t(lang, "payment_preparing_novapay")}
            </p>
          ) : null}
        </div>
      ) : null}

      {isFop && !paidVerified ? (
        <div className="sv2-info-card sv2-fop-compact" style={{ marginBottom: 12 }} data-testid="sv2-fop-payment-card">
          <p className="sv2-muted" data-testid="sv2-bank-panel-awaiting" style={{ margin: "8px 10px" }}>
            {bankStillPending ? t(lang, "bank_awaiting_credit") : t(lang, "bank_awaiting_confirmation")}
          </p>
          {bankStillPending && !paidVerified ? (
            <p className="sv2-muted" data-testid="sv2-bank-awaiting-credit-body" style={{ margin: "0 10px 8px" }}>
              {t(lang, "bank_awaiting_credit_body")}
            </p>
          ) : null}
          {(bankCreatedAt || bankConfirmedAt) ? (
            <ol
              className="sv2-muted"
              data-testid="sv2-bank-payment-timeline"
              style={{ margin: "0 10px 8px", paddingLeft: 18, fontSize: 13 }}
            >
              {bankCreatedAt ? (
                <li>
                  {t(lang, "bank_timeline_created")} {formatKyivClock(bankCreatedAt)}
                </li>
              ) : null}
              {bankCreatedAt ? (
                <li>
                  {t(lang, "bank_timeline_awaiting")} {formatKyivClock(bankCreatedAt)}
                </li>
              ) : null}
              {bankConfirmedAt ? (
                <li>
                  {t(lang, "bank_timeline_confirmed")} {formatKyivClock(bankConfirmedAt)}
                </li>
              ) : null}
            </ol>
          ) : null}
          {bankChecking && !paidVerified ? (
            <p className="sv2-muted" data-testid="sv2-bank-checking" style={{ margin: "0 10px 8px" }}>
              {t(lang, "bank_checking_payment")}
            </p>
          ) : null}
          {bankStillPending && !paidVerified ? (
            <div data-testid="sv2-bank-still-pending" style={{ margin: "0 10px 8px" }}>
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                onClick={() => {
                  setBankChecking(true);
                  void pollAsiaStyleBankPaymentStatus({ sessionKey, orderId }).then((result) => {
                    setBankChecking(false);
                    if (result.created_at) setBankCreatedAt(result.created_at);
                    if (result.bank_confirmed_at) setBankConfirmedAt(result.bank_confirmed_at);
                    if (result.paid) {
                      setBankStillPending(false);
                      markBankPaymentConfirmed();
                    }
                  });
                }}
              >
                {t(lang, "bank_refresh_status")}
              </button>
            </div>
          ) : null}
          <BankPaymentDetailsV2
            lang={lang}
            testId="sv2-bank-panel"
            fields={{
              amountLabel,
              purpose,
              seller: instruction?.seller ?? STORE_INFO.merchantName,
              rnokpp: instruction?.rnokpp ?? STORE_INFO.edrpou,
              bank: instruction?.bank ?? STORE_INFO.bank,
              iban: instruction?.iban ?? STORE_INFO.iban,
              ibanDisplay: instruction?.iban_display ?? STORE_INFO.ibanDisplay,
              publicOrderNumber: instruction?.public_order_number ?? displayOrderNumber ?? null,
            }}
          />
          <div className="sv2-pay-actions" style={{ display: "grid", gap: 8, margin: "0 10px 10px" }}>
            <BankPayActionsV2
              lang={lang}
              payload={instruction}
              testIdPrefix="sv2-bank-panel"
              autoAttemptHandoff={autoAttemptBankHandoff}
              paymentConfirmed={paidVerified}
            />
            <button
              type="button"
              className="sv2-cta sv2-secondary sv2-fop-requisites-cta"
              data-testid="sv2-fop-send-telegram-requisites"
              style={{ whiteSpace: "normal", lineHeight: 1.25, padding: "12px 14px" }}
              disabled={status === "sending"}
              onClick={() => {
                void (async () => {
                  setStatus("sending");
                  setMsg(null);
                  const res = await customerOrderActionV2({
                    sessionKey,
                    orderId,
                    action: "send_fop_requisites",
                  });
                  if (res.ok) {
                    const delivery = String(
                      (res as { delivery_state?: string }).delivery_state ?? "",
                    ).toLowerCase();
                    setStatus("idle");
                    setMsg(
                      delivery === "recently_sent"
                        ? t(lang, "requisites_sent_chat")
                        : (res.message ?? t(lang, "requisites_sent_chat")),
                    );
                  } else {
                    setStatus("error");
                    setMsg(
                      res.message ??
                        (lang === "en"
                          ? "Could not send payment details. Try again."
                          : "Не вдалося надіслати реквізити. Спробуйте ще раз."),
                    );
                  }
                })();
              }}
            >
              {status === "sending"
                ? lang === "en"
                  ? "Sending…"
                  : "Надсилаємо…"
                : t(lang, "send_requisites_chat")}
            </button>
            {msg && isFop ? (
              <p
                className={status === "error" ? "sv2-error" : "sv2-muted"}
                data-testid="sv2-fop-send-feedback"
                style={{ fontSize: 13, marginTop: 0 }}
              >
                {msg}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      {isFop && paidVerified ? (
        <div className="sv2-info-card" style={{ marginBottom: 12 }} data-testid="sv2-fop-paid-summary">
          <p style={{ margin: "8px 10px", fontWeight: 600 }} data-testid="sv2-fop-paid-title">
            {t(lang, "bank_paid_title")}
          </p>
          {(bankConfirmedAtProp || bankConfirmedAt) ? (
            <p className="sv2-muted" style={{ margin: "0 10px 10px", fontSize: 13 }} data-testid="sv2-fop-paid-compact">
              {formatKyivClock(bankConfirmedAtProp ?? bankConfirmedAt)}
              {totalUah != null ? ` · ${formatUah(totalUah)}` : ""}
            </p>
          ) : null}
        </div>
      ) : null}
      {showFopReceiptCta ? (
        <div className="sv2-pay-actions" style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-fop-send-receipt"
            disabled={status === "sending" && !receiptFile}
            onClick={() => setReviewOpen(true)}
          >
            {status === "receipt_uploaded"
              ? openReviewLabel
              : paymentLocked
                ? lang === "en"
                  ? "Retry receipt upload"
                  : "Повторити надсилання квитанції"
                : openReviewLabel}
          </button>
          {status === "receipt_uploaded" ? (
            <p className="sv2-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="sv2-fop-receipt-added">
              {lang === "en" ? "Receipt added" : "Квитанцію додано"}
            </p>
          ) : !paymentLocked ? (
            <p className="sv2-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="sv2-fop-send-hint">
              {t(lang, paidVerified ? "fop_send_receipt_hint_paid" : "fop_send_receipt_hint")}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="sv2-pay-actions">
        {showChangeMethod ? (
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-change-payment-method"
            disabled={status === "sending"}
            onClick={() =>
              void run(
                "change_payment_method",
                isFop ? "nova_poshta_cod" : isCod ? "novapay_card" : "bank_transfer_fop",
              )
            }
          >
            {isFop
              ? lang === "en"
                ? "Change payment method → COD"
                : "Змінити спосіб оплати → післяплата"
              : isCod
                ? lang === "en"
                  ? "Change payment method → online"
                  : "Змінити спосіб оплати → онлайн"
                : lang === "en"
                  ? "Change payment method → FOP"
                  : "Змінити спосіб оплати → рахунок ФОП"}
          </button>
        ) : null}
        {paymentFailed && onOpenLegalHub ? (
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-payment-failure-legal-hub"
            style={{ marginTop: 8 }}
            onClick={onOpenLegalHub}
          >
            {lang === "en" ? "Payment & delivery (legal)" : "Оплата і доставка (документи)"}
          </button>
        ) : null}
      </div>
      {status === "error" && msg ? <div className="sv2-alert sv2-alert-err">{msg}</div> : null}
      {status === "receipt_uploaded" || (isFop && paymentLocked && status !== "receipt_fallback") ? (
        <div className="sv2-info-card" data-testid="sv2-fop-review-status" style={{ marginTop: 12 }}>
          <strong data-testid="sv2-fop-receipt-sent">{t(lang, "fop_receipt_sent")}</strong>
          {!paidVerified ? (
            <>
              <div className="sv2-muted" data-testid="sv2-fop-awaiting-manager">
                {t(lang, "fop_awaiting_manager")}
              </div>
              <p className="sv2-muted" style={{ fontSize: 12, marginTop: 8 }}>
                {lang === "en"
                  ? "A receipt alone does not prove payment. Method is locked until a manager verifies the transfer."
                  : "Квитанція сама по собі не підтверджує оплату. Спосіб оплати заблоковано до перевірки менеджером."}
              </p>
            </>
          ) : (
            <p className="sv2-muted" style={{ fontSize: 12, marginTop: 8 }}>
              {lang === "en"
                ? "Optional — the receipt is saved on the order. The bank confirmation is already complete."
                : "Необов’язково — квитанція збережена в замовленні. Банк уже підтвердив оплату."}
            </p>
          )}
        </div>
      ) : null}
      {status === "receipt_fallback" && msg ? <div className="sv2-alert sv2-alert-err">{msg}</div> : null}

      <PaymentConfirmationSheet
        lang={lang}
        variant="inline"
        open={reviewOpen}
        orderNo={orderNo}
        amountLabel={amountLabel}
        paymentComment={paymentComment}
        receiptPreview={receiptPreview}
        receiptFileName={receiptFile?.name ?? null}
        receiptFileSize={receiptFile?.size ?? null}
        busy={status === "sending"}
        uploadProgress={uploadProgress}
        onClose={() => {
          onReceiptSelected(null);
          setPaymentComment("");
          setReviewOpen(false);
        }}
        onCommentChange={setPaymentComment}
        onReceiptSelected={onReceiptSelected}
        onSubmit={() => void submitPaymentReview()}
      />
    </div>
  );
}
